/**
 * Polyfills pour les runtimes Chromium anterieurs a la version 98.
 *
 * Le kiosque Electron installe sur le terrain embarque Chromium 96 (Electron
 * 16) alors que le front, lui, est servi depuis le web et donc mis a jour
 * immediatement. Depuis la montee de version des dependances,
 * @azure/communication-react et @azure/communication-calling utilisent des
 * primitives ES2022/ES2023 absentes de Chromium 96 :
 *   - Array.prototype.findLast / findLastIndex (Chrome 97) : utilisees par le
 *     VideoGallery des qu'un participant distant est connecte, ce qui rendait
 *     la page blanche au passage de l'appel en "accepte".
 *   - structuredClone (Chrome 98) : utilise par la couche d'encodage video.
 *
 * Ce fichier doit rester le tout premier import de src/index.tsx.
 * Il pourra etre supprime une fois l'ensemble du parc migre sur le client
 * Electron 44.
 */

function definePrototypeMethod(
  target: object,
  name: string,
  value: (...args: never[]) => unknown
): void {
  if (name in target) {
    return;
  }
  Object.defineProperty(target, name, {
    value,
    writable: true,
    configurable: true,
    enumerable: false,
  });
}

definePrototypeMethod(Array.prototype, "findLast", function findLast(
  this: unknown[],
  predicate: (value: unknown, index: number, array: unknown[]) => unknown,
  thisArg?: unknown
) {
  for (let i = this.length - 1; i >= 0; i--) {
    if (predicate.call(thisArg, this[i], i, this)) {
      return this[i];
    }
  }
  return undefined;
} as (...args: never[]) => unknown);

definePrototypeMethod(Array.prototype, "findLastIndex", function findLastIndex(
  this: unknown[],
  predicate: (value: unknown, index: number, array: unknown[]) => unknown,
  thisArg?: unknown
) {
  for (let i = this.length - 1; i >= 0; i--) {
    if (predicate.call(thisArg, this[i], i, this)) {
      return i;
    }
  }
  return -1;
} as (...args: never[]) => unknown);

if (typeof globalThis.structuredClone !== "function") {
  const deepClone = (value: unknown, seen: Map<unknown, unknown>): unknown => {
    if (value === null || typeof value !== "object") {
      return value;
    }
    const cached = seen.get(value);
    if (cached !== undefined) {
      return cached;
    }
    if (value instanceof Date) {
      return new Date(value.getTime());
    }
    if (value instanceof RegExp) {
      return new RegExp(value.source, value.flags);
    }
    if (Array.isArray(value)) {
      const copy: unknown[] = [];
      seen.set(value, copy);
      value.forEach((item) => copy.push(deepClone(item, seen)));
      return copy;
    }
    if (value instanceof Map) {
      const copy = new Map();
      seen.set(value, copy);
      value.forEach((v, k) => copy.set(deepClone(k, seen), deepClone(v, seen)));
      return copy;
    }
    if (value instanceof Set) {
      const copy = new Set();
      seen.set(value, copy);
      value.forEach((v) => copy.add(deepClone(v, seen)));
      return copy;
    }
    if (ArrayBuffer.isView(value) || value instanceof ArrayBuffer) {
      // Les vues typees exposent slice(), qui copie le buffer sous-jacent.
      return (value as unknown as { slice: () => unknown }).slice();
    }
    const copy: Record<string, unknown> = {};
    seen.set(value, copy);
    Object.keys(value).forEach((key) => {
      copy[key] = deepClone((value as Record<string, unknown>)[key], seen);
    });
    return copy;
  };

  globalThis.structuredClone = ((value: unknown) =>
    deepClone(value, new Map())) as typeof structuredClone;
}

export {};
